import React, { useEffect, useState } from "react";
import { CloudUpload, RefreshCw, Trash2, AlertTriangle, CheckCheck } from "lucide-react";
import { getQueue, removeFromQueue, drainQueue, subscribe, updateQueueItem } from "@/lib/offline";
import { useOnlineStatus } from "@/lib/useOnlineStatus";
import { fmtDate } from "@/lib/api";
import { toast } from "sonner";

export default function SyncQueue() {
  const [items, setItems] = useState([]);
  const online = useOnlineStatus();

  const load = () => getQueue().then(setItems);
  useEffect(() => {
    load();
    const off = subscribe(({ queue }) => setItems(queue));
    return off;
  }, []);

  const retry = async (id) => {
    await updateQueueItem(id, { status: "pending", last_error: null });
    const res = await drainQueue();
    toast.message(`Synced ${res.succeeded || 0} · ${res.failed || 0} still pending`);
    load();
  };

  const drop = async (id) => {
    if (!window.confirm("Discard this pending operation? It will not be sent.")) return;
    await removeFromQueue(id);
    toast.success("Removed from queue");
  };

  const syncAll = async () => {
    const res = await drainQueue();
    if (res.skipped) return toast.message("Cannot sync now — you may be offline");
    toast.success(`Synced ${res.succeeded}, ${res.failed} pending retry`);
  };

  return (
    <div className="space-y-6" data-testid="sync-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Offline</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Pending sync</h1>
          <p className="text-sm text-zinc-500 mt-2">Operations queued while offline. They are automatically replayed when you reconnect.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs uppercase tracking-wider px-2 py-1 border ${online ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
            {online ? "Online" : "Offline"}
          </span>
          <button data-testid="sync-all-button" onClick={syncAll} disabled={!online || items.length === 0} className="px-4 py-2 bg-[#002FA7] text-white text-xs uppercase tracking-wider hover:bg-blue-900 disabled:opacity-50 inline-flex items-center gap-2">
            <CloudUpload className="w-3 h-3" /> Sync now
          </button>
        </div>
      </div>

      <div className="brutalist-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-semibold">Queued</th>
              <th className="text-left px-5 py-3 font-semibold">Action</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-left px-5 py-3 font-semibold">Error</th>
              <th className="text-right px-5 py-3 font-semibold">Attempts</th>
              <th className="text-right px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500"><CheckCheck className="w-6 h-6 mx-auto mb-2 text-emerald-400" />All caught up — no pending sync.</td></tr>
            )}
            {items.map((i) => (
              <tr key={i.id} className="border-t border-zinc-200" data-testid={`sync-row-${i.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{fmtDate(i.queued_at)}</td>
                <td className="px-5 py-3">
                  <div className="text-zinc-950 font-medium">{i.label}</div>
                  <div className="text-[11px] font-mono text-zinc-500">{i.method} {i.url}</div>
                </td>
                <td className="px-5 py-3">
                  {i.status === "failed_permanent"
                    ? <span className="text-[11px] uppercase tracking-wider px-2 py-1 border border-rose-200 bg-rose-50 text-rose-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Failed</span>
                    : <span className="text-[11px] uppercase tracking-wider px-2 py-1 border border-amber-200 bg-amber-50 text-amber-700">Pending</span>}
                </td>
                <td className="px-5 py-3 text-xs text-zinc-500 max-w-md truncate">{i.last_error || "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{i.attempts}</td>
                <td className="px-5 py-3 text-right space-x-1">
                  <button data-testid={`sync-retry-${i.id}`} disabled={!online} onClick={() => retry(i.id)} className="text-xs uppercase tracking-wider px-2 py-1 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Retry</button>
                  <button data-testid={`sync-drop-${i.id}`} onClick={() => drop(i.id)} className="text-xs uppercase tracking-wider px-2 py-1 border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Drop</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
